import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserStub } from './components/BrowserStub';
import { Header } from './components/Header';
import { LoadingScreen } from './components/LoadingScreen';
import { useTelegram } from './hooks/useTelegram';
import { api, ApiError, newIdempotencyKey, type PublicSettings, type Role, type WithdrawalDto } from './lib/api';
import * as sfx from './lib/sounds';
import {
    INITIAL_NOTIFS,
    type CaseDef,
    type ModalId,
    type Notification,
    type ServerTask,
    type ViewId,
    type WithdrawRecord,
} from './state';

import { MenuView } from './views/Menu';
import { GamesView } from './views/Games';
import { BoxesView } from './views/Boxes';
import { BoxGameView } from './views/BoxGame';
import { FastGiveawayView } from './views/FastGiveaway';
import { BogBattleView } from './views/BogBattle';
import { BattleLobbyView } from './views/BattleLobby';
import { Game667View } from './views/Game667';
import { PiggyBankView } from './views/PiggyBank';
import { ProfileView } from './views/Profile';
import { BonusesView } from './views/Bonuses';
import { BigGiveawayView } from './views/BigGiveaway';
import { AdminPanelView } from './views/AdminPanel';

import { NotificationsModal } from './modals/Notifications';
import { WithdrawModal } from './modals/Withdraw';
import { CreateBattleModal } from './modals/CreateBattle';
import { CreateTaskModal } from './modals/CreateTask';
import { TaskExecModal, type TaskResult } from './modals/TaskExec';
import { AppealReviewModal } from './modals/AppealReview';
import { VipWarningModal } from './modals/VipWarning';
import { GiveawayRulesModal } from './modals/GiveawayRules';
import { ConfirmDeleteModal } from './modals/ConfirmDelete';
import { ResultPopup } from './modals/ResultPopup';
import { EmojiPickerModal } from './modals/EmojiPicker';
import { TopUpModal } from './modals/TopUp';

/**
 * Top-level shell.
 *
 * Backend integration so far:
 *   * /api/me/role on boot → admin entry-point in profile if root/admin/checker.
 *   * /api/withdrawals/me on profile mount → real withdrawal history.
 *   * /api/withdrawals on submit → real balance debit + queue insert.
 *   * /api/battles list/create/join used by BogBattleView.
 * Other sections (boxes, fast, 667, tasks) still run on local mock state and
 * will land in subsequent PRs.
 *
 * Loading splash: shown until `useTelegram` resolves and the component has
 * had at least 700 ms on screen. Avoids the harsh black flash some Telegram
 * WebViews show when the bridge is fast.
 */
export function App() {
    const { isReady, user: tgUser, startParam, webApp } = useTelegram();
    const [bootElapsed, setBootElapsed] = useState(false);

    // Outside Telegram (plain browser visit): show the bot-bounce landing.
    // The dev override (?dev=1) and Vite dev-server hostnames keep the app
    // working for Playwright / local previews where there is no WebApp bridge.
    const inBrowser = !webApp && !isDevHost();

    // View / modal stack
    const [view, setView] = useState<ViewId>('menu');
    const [modal, setModal] = useState<ModalId | null>(null);
    const [activeCase, setActiveCase] = useState<CaseDef | null>(null);
    const [activeTask, setActiveTask] = useState<ServerTask | null>(null);
    const [tasksVersion, setTasksVersion] = useState(0);
    const [activeBattleId, setActiveBattleId] = useState<number | null>(null);
    const [resultPayload, setResultPayload] = useState<{
        kind: 'boxWin' | 'fastLose' | 'fastNoFunds' | 'withdrawQueued' | 'taskDone' | 'taskPending'
            | 'taskHolding' | 'taskAppeal' | 'taskCreated';
        amount?: number;
        /** taskAppeal: which round; taskCreated: 1 = went to moderation. */
        detail?: number;
        holdUntil?: string;
    } | null>(null);

    // Mock-or-server domain state ----------------------------------------
    const [balance, setBalance] = useState(0);
    const [notifs, setNotifs] = useState<Notification[]>(INITIAL_NOTIFS);
    /** Unread count straight from the server (`/api/notifications` returns
     *  `unread`). Drives the bell badge so "mark all read" actually clears
     *  it instead of showing total-notif count forever. */
    const [unreadCount, setUnreadCount] = useState(0);
    const [avatar, setAvatar] = useState('😎');
    const [isAnon, setIsAnon] = useState(false);
    const [withdrawals, setWithdrawals] = useState<WithdrawRecord[]>([]);
    const [fastBank, setFastBank] = useState(4102);
    const [bigBank] = useState(1391);
    const [profile, setProfile] = useState<{
        userId: number; name: string; username: string; joinedAt: string;
        withdrawn: number; bonusesEarned: number;
    }>({
        userId: 0,
        name: 'Игрок',
        username: '',
        joinedAt: '',
        withdrawn: 0,
        bonusesEarned: 0,
    });

    /* ------ Hydrate profile from Telegram WebApp user before the server
            round-trip resolves. Avoids the "default name" flash. */
    useEffect(() => {
        if (!tgUser) return;
        setProfile((p) => ({
            ...p,
            userId: tgUser.id || p.userId,
            name: tgUser.first_name || tgUser.username || p.name || `id${tgUser.id}`,
            username: tgUser.username || p.username || `id${tgUser.id}`,
        }));
    }, [tgUser]);

    // Server-driven role for admin gate. Falls through to 'user' if API
    // unreachable so the rest of the UI still works in dev/offline.
    const [role, setRole] = useState<Role>('user');

    // Public knobs (commission %, ticket prices, min withdraw). Loaded once
    // and re-pulled when the user opens the top-up / create-task flow.
    const [settings, setSettings] = useState<PublicSettings | null>(null);
    const [botUsername, setBotUsername] = useState<string>('BogStarsBot');

    const [isOnline, setIsOnline] = useState(navigator.onLine);
    /** Auto-open the BIG GIVEAWAY rules popup once when arrived from the
     *  channel deeplink. Reset after the popup is dismissed so further
     *  navigations to the view don't keep re-opening it. */
    const [bigAutoRules, setBigAutoRules] = useState(false);
    const [freshRefs, setFreshRefs] = useState(0);
    // User's own referral code + how many they've invited. Loaded at boot
    // alongside profile/balance so Bonuses → Рефералы renders the correct
    // invite link immediately instead of the hardcoded placeholder.
    const [refCode, setRefCode] = useState('');
    const [refCount, setRefCount] = useState(0);

    const giveawayTarget = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 5);
        return d;
    }, []);

    /* ------ Boot timing ------ */
    useEffect(() => {
        const id = window.setTimeout(() => setBootElapsed(true), 800);
        return () => window.clearTimeout(id);
    }, []);

    /* ------ Online banner ------ */
    useEffect(() => {
        const on = () => setIsOnline(true);
        const off = () => setIsOnline(false);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        return () => {
            window.removeEventListener('online', on);
            window.removeEventListener('offline', off);
        };
    }, []);

    /* ------ Bootstrap from server: me + notifs + withdrawal history ------ */
    useEffect(() => {
        if (!isReady) return;
        let cancelled = false;
        (async () => {
            try {
                const me = await api.getMe();
                if (cancelled) return;
                setRole(me.role);
                setBalance(me.balance);
                setIsAnon(me.isAnon);
                setAvatar(me.avatar);
                setFreshRefs(me.freshRefsUnused);
            } catch {
                /* offline / no telegram bridge — keep defaults. */
            }
            try {
                const st = await api.getState();
                if (cancelled) return;
                setProfile((p) => ({
                    userId: st.user.id,
                    name: st.user.firstName ?? tgUser?.first_name ?? p.name ?? `id${st.user.id}`,
                    username: st.user.username ?? tgUser?.username ?? p.username ?? `id${st.user.id}`,
                    joinedAt: new Date(st.user.joinedAt).toLocaleDateString('ru-RU'),
                    withdrawn: st.user.withdrawnTotal ?? 0,
                    bonusesEarned: st.user.bonusesEarned ?? 0,
                }));
                if (st.user.refCode) setRefCode(st.user.refCode);
            } catch { /* keep Telegram-derived defaults */ }
            try {
                const r = await api.getReferrals();
                if (cancelled) return;
                if (r.code) setRefCode(r.code);
                setRefCount(r.totalRefs);
            } catch { /* offline */ }
            try {
                const w = await api.listMyWithdrawals();
                if (!cancelled) setWithdrawals(w.items.map(toLocalWithdrawal));
            } catch { /* empty */ }
            try {
                const s = await api.publicSettings();
                if (!cancelled) {
                    setSettings(s.settings);
                    if (s.botUsername) setBotUsername(s.botUsername);
                }
            } catch { /* fall back to CreateTask defaults */ }
            try {
                const n = await api.listNotifications();
                if (!cancelled) {
                    setNotifs(n.items.map((row) => ({
                        id: row.id,
                        text: row.text,
                        accent: row.kind.startsWith('withdraw_paid') ? 'green'
                              : row.kind.startsWith('withdraw_rejected') || row.kind.startsWith('battle_lost') ? 'red'
                              : row.kind === 'battle_won' || row.kind === 'fast_won' || row.kind === 'big_won' ? 'gold'
                              : 'blue',
                    })));
                    setUnreadCount(n.unread);
                }
            } catch { /* keep mock */ }
        })();
        return () => { cancelled = true; };
    }, [isReady]);

    /* ------ Save avatar / isAnon to server on change ------ */
    useEffect(() => {
        if (!isReady) return;
        const id = setTimeout(() => {
            api.patchProfile({ avatar, isAnon }).catch(() => {});
        }, 400);
        return () => clearTimeout(id);
    }, [avatar, isAnon, isReady]);

    /* ------ Bind referral from start_param once ------ */
    useEffect(() => {
        if (!isReady || !startParam) return;
        // Channel-button deeplinks: route into the matching view rather than
        // treating the prefix-tagged param as a referral code. Plain refs
        // (no recognised prefix) still go through bindReferral.
        if (/^battle(?:_\d+)?$/.test(startParam)) {
            setView('bogBattle');
            return;
        }
        if (/^(big|big_\d+)$/.test(startParam)) {
            setView('bigGiveaway');
            setBigAutoRules(true);
            return;
        }
        if (startParam === 'fast') {
            setView('fastGiveaway');
            return;
        }
        if (startParam === 'piggy') {
            setView('piggyBank');
            return;
        }
        // Idempotent server-side — the bind no-ops if user already has a
        // referrer, so retries on every reload are fine.
        api.bindReferral(startParam).catch(() => {});
    }, [isReady, startParam]);

    /* ------ Poll notifications so new server-side events (admin paid your
            withdrawal, root_grant, big win, broadcast) appear without a
            manual refresh. Stopped when the tab is hidden — but we ALSO
            refresh immediately on visibilitychange/focus so coming back to
            the app feels instant instead of waiting for the next tick.
            10s cadence is the sweet spot: an admin granting balance sees the
            user pill update within ~10s instead of "ничего не пришло, проект
            на коленке". If any new notification implies a balance change
            (deposit_paid, withdraw_paid, battle_won, big_won, fast_won,
            task_completed, root_grant), pull /api/me once so the header pill
            reflects the new amount without waiting for the user to navigate. */
    const refreshNotifsRef = useRef<() => Promise<void>>(async () => {});
    useEffect(() => {
        if (!isReady) return;
        let stop = false;
        let knownIds = new Set<number>(notifs.map((n) => n.id));
        let firstTick = true;
        const BALANCE_KINDS = new Set([
            'deposit_paid', 'withdraw_paid', 'withdraw_rejected',
            'battle_won', 'piggy_won', 'big_won', 'fast_won',
            'task_completed', 'root_grant',
        ]);
        const tick = async () => {
            if (document.hidden) return;
            try {
                const n = await api.listNotifications();
                if (stop) return;
                const balanceTouched = n.items.some(
                    (row) => !knownIds.has(row.id) && BALANCE_KINDS.has(row.kind),
                );
                // Detect actual new notifications (before we overwrite knownIds)
                // so the ding only fires on freshly-arrived events, not on
                // every poll tick. Skip the very first tick — knownIds is
                // empty on mount and we don't want a sound for stale items.
                const hasNewNotifs = !firstTick && n.items.some((row) => !knownIds.has(row.id));
                firstTick = false;
                knownIds = new Set(n.items.map((r) => r.id));
                setNotifs(n.items.map((row) => ({
                    id: row.id,
                    text: row.text,
                    accent:
                        row.kind === 'withdraw_paid' || row.kind === 'piggy_won' ? 'green'
                      : row.kind === 'withdraw_rejected' || row.kind === 'battle_lost' || row.kind === 'piggy_lost' || row.kind === 'task_failed' ? 'red'
                      : row.kind === 'battle_won' || row.kind === 'fast_won' || row.kind === 'big_won' ? 'gold'
                      : 'blue',
                })));
                if (hasNewNotifs) sfx.play('notify');
                setUnreadCount(n.unread);
                if (balanceTouched) {
                    api.getMe().then((me) => {
                        if (stop) return;
                        setBalance(me.balance);
                        setFreshRefs(me.freshRefsUnused);
                    }).catch(() => {});
                }
            } catch { /* offline */ }
        };
        refreshNotifsRef.current = tick;
        const id = window.setInterval(tick, 10_000);
        const onWake = () => {
            if (document.hidden) return;
            void tick();
            // Also refresh the balance pill directly — covers the case where
            // a user returns from an external CryptoBot tab and the
            // deposit_paid notif hasn't propagated yet.
            api.getMe().then((me) => {
                if (stop) return;
                setBalance(me.balance);
                setFreshRefs(me.freshRefsUnused);
            }).catch(() => {});
        };
        document.addEventListener('visibilitychange', onWake);
        window.addEventListener('focus', onWake);
        return () => {
            stop = true;
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onWake);
            window.removeEventListener('focus', onWake);
        };
    }, [isReady]);

    const goView = useCallback((id: ViewId) => setView(id), []);
    const closeModal = useCallback(() => setModal(null), []);

    const isAdmin = role === 'admin' || role === 'root' || role === 'checker';

    // Pre-cache common SFX once on mount. play() is a no-op if the matching
    // .mp3 in /public/sounds/ doesn't exist yet — safe to wire even before
    // the audio assets land in the repo.
    useEffect(() => { sfx.prewarm(); }, []);

    /**
     * After a top-up attempt, ping /api/me until balance grows (or we time
     * out). Three races make this necessary:
     *   1) openInvoice('paid') can fire BEFORE the bot webhook lands.
     *   2) CryptoBot is `pending` always — user pays in an external tab
     *      and webhook arrives whenever it arrives.
     *   3) Notification poll is on a 10s cadence; pill should react faster.
     * We schedule a few short retries with growing gaps (1s, 2s, 4s, 7s, 12s)
     * — cumulatively ~26s of patience, no more.
     */
    const pollBalanceForCredit = useCallback(async (expectedDelta: number) => {
        const before = balance;
        const delays = [1000, 2000, 4000, 7000, 12000];
        for (const wait of delays) {
            await new Promise((r) => setTimeout(r, wait));
            try {
                const me = await api.getMe();
                if (me.balance > before) {
                    setBalance(me.balance);
                    setFreshRefs(me.freshRefsUnused);
                    sfx.play('coin');
                    return;
                }
            } catch { /* network blip — keep trying */ }
        }
        // Last attempt: refresh regardless so users at least see the latest
        // server-side number even if no delta was detected.
        try {
            const me = await api.getMe();
            setBalance(me.balance);
            setFreshRefs(me.freshRefsUnused);
        } catch { /* offline */ }
        // expectedDelta is currently informational — kept in the signature so
        // we can show a toast in the future when the delta actually matches.
        void expectedDelta;
    }, [balance]);

    /* ------ Box game (still mock) ------ */
    const handlePickCase = (c: CaseDef) => {
        setActiveCase(c);
        setView('boxGame');
    };
    const handleBoxWin = (value: number, cost: number) => {
        setBalance((b) => b - cost + value);
        sfx.play(value > cost ? 'win' : 'lose');
        setResultPayload({ kind: 'boxWin', amount: value });
    };

    /* ------ Fast giveaway (server) ------ */
    const handleBuyTicket = async () => {
        try {
            const r = await api.fastBuy(newIdempotencyKey());
            setBalance(r.balance);
            if (r.won) {
                sfx.play('big-win');
                setResultPayload({ kind: 'boxWin', amount: r.payout });
            } else {
                sfx.play('lose');
                setResultPayload({ kind: 'fastLose' });
            }
            try {
                const s = await api.getFast();
                setFastBank(s.bank);
            } catch { /* ignore */ }
        } catch (err) {
            // Insufficient-balance → tell the user explicitly and offer the
            // top-up modal. Without this branch the click was a silent no-op
            // (the local-mock fallback below also returned without feedback),
            // so the screen looked like "FAST giveaway не работает".
            if (err instanceof ApiError && err.code === 'insufficient_balance') {
                setResultPayload({ kind: 'fastNoFunds' });
                return;
            }
            // Local-mock fallback so a no-bot dev preview still works.
            const TICKET = 5;
            if (balance < TICKET) {
                setResultPayload({ kind: 'fastNoFunds' });
                return;
            }
            setBalance((b) => b - TICKET);
            const won = Math.random() < 0.01;
            if (won) {
                setBalance((b) => b + fastBank);
                setResultPayload({ kind: 'boxWin', amount: fastBank });
                setFastBank(0);
            } else {
                setFastBank((b) => b + TICKET);
                setResultPayload({ kind: 'fastLose' });
            }
        }
    };

    /* ------ Bonuses (server) ------ */
    const handleExecTask = (t: ServerTask) => {
        setActiveTask(t);
        setModal('taskExec');
    };
    const handleTaskComplete = async (t: ServerTask, result: TaskResult) => {
        setActiveTask(null);
        setModal(null);
        // Reload the task list so button states update immediately
        setTasksVersion((v) => v + 1);
        switch (result.kind) {
            case 'pending':
                setResultPayload({ kind: 'taskPending' });
                return;
            case 'holding':
                setResultPayload({ kind: 'taskHolding', amount: t.reward, holdUntil: result.holdUntil });
                return;
            case 'appeal':
                setResultPayload({ kind: 'taskAppeal', detail: result.round });
                return;
            case 'paid':
                try {
                    const me = await api.getMe();
                    setBalance(me.balance);
                } catch { setBalance((b) => b + result.amount); }
                setResultPayload({ kind: 'taskDone', amount: result.amount });
        }
    };

    /* ------ Withdraw (real backend) ------ */
    const handleWithdrawSubmitted = async ({ amount, balance: newBal }: {
        amount: number; balance: number | null;
    }) => {
        if (typeof newBal === 'number') setBalance(newBal);
        else setBalance((b) => b - amount);
        try {
            const w = await api.listMyWithdrawals();
            setWithdrawals(w.items.map(toLocalWithdrawal));
        } catch { /* keep cached list */ }
        setResultPayload({ kind: 'withdrawQueued', amount });
        setModal(null);
    };

    const showLoading = !isReady || !bootElapsed;

    if (inBrowser) {
        return <BrowserStub botUsername={botUsername} />;
    }

    return (
        <>
            {showLoading && <LoadingScreen />}

            <div className="app-container" aria-hidden={showLoading}>
                <Header
                    balance={balance}
                    notifCount={unreadCount}
                    onBellClick={() => {
                        // Force-refresh on open so any pending admin grant /
                        // withdraw_paid / win event arrives instantly instead
                        // of waiting for the next poll tick.
                        void refreshNotifsRef.current();
                        setModal('notifications');
                    }}
                    onPlusClick={() => setModal('topUp')}
                    onProfileClick={() => setView('profile')}
                    onAdminClick={isAdmin ? () => setView('admin') : undefined}
                />

                {!isOnline && (
                    <div className="offline-banner" style={{ margin: '0 18px' }}>
                        Нет соединения с сервером.
                    </div>
                )}

                {view === 'menu' && (
                    <MenuView
                        onNavigate={goView}
                        giveawayTarget={giveawayTarget}
                        notifBadge={notifs.length}
                    />
                )}
                {view === 'games' && <GamesView onNavigate={goView} />}
                {view === 'boxes' && <BoxesView onNavigate={goView} onPickCase={handlePickCase} />}
                {view === 'boxGame' && activeCase && (
                    <BoxGameView
                        caseDef={activeCase}
                        balance={balance}
                        onNavigate={goView}
                        onWin={handleBoxWin}
                    />
                )}
                {view === 'fastGiveaway' && (
                    <FastGiveawayView
                        onNavigate={goView}
                        onBuyTicket={handleBuyTicket}
                        bank={fastBank}
                        ticketPrice={5}
                    />
                )}
                {view === 'bogBattle' && (
                    <BogBattleView
                        onNavigate={goView}
                        onCreate={() => setModal('createBattle')}
                        myId={tgUser?.id ?? profile.userId ?? null}
                        botUsername={botUsername}
                        onEnterRoom={(id) => { setActiveBattleId(id); setView('battleLobby'); }}
                    />
                )}
                {view === 'battleLobby' && activeBattleId && (
                    <BattleLobbyView
                        battleId={activeBattleId}
                        myId={tgUser?.id ?? profile.userId ?? null}
                        onNavigate={goView}
                        botUsername={botUsername}
                        onAfterResolve={() => {
                            api.getMe().then((me) => setBalance(me.balance)).catch(() => {});
                        }}
                    />
                )}
                {view === 'game667' && (
                    <Game667View
                        onNavigate={goView}
                        balance={balance}
                        onSettled={(newBalance, won, payout) => {
                            setBalance(newBalance);
                            sfx.play(won ? 'big-win' : 'lose');
                            if (won && payout > 0) {
                                setResultPayload({ kind: 'boxWin', amount: payout });
                            }
                        }}
                    />
                )}
                {view === 'piggyBank' && (
                    <PiggyBankView
                        onNavigate={goView}
                        onBalanceChanged={setBalance}
                    />
                )}
                {view === 'profile' && (
                    <ProfileView
                        onNavigate={goView}
                        onOpenWithdraw={() => setModal('withdraw')}
                        onOpenEmojiPicker={() => setModal('profileEmoji')}
                        avatar={avatar}
                        setAvatar={setAvatar}
                        name={profile.name}
                        username={profile.username}
                        userId={profile.userId}
                        joinedAt={profile.joinedAt}
                        withdrawn={
                            profile.withdrawn ||
                            withdrawals.filter((w) => w.status === 'sent').reduce((s, w) => s + w.amount, 0)
                        }
                        earnedBonuses={profile.bonusesEarned}
                        isAnon={isAnon}
                        setIsAnon={setIsAnon}
                        history={withdrawals}
                        adminEntry={isAdmin ? { role, onOpen: () => setView('admin') } : null}
                    />
                )}
                {view === 'bonuses' && (
                    <BonusesView
                        onNavigate={goView}
                        onExecTask={handleExecTask}
                        onCreateTask={() => setModal('createTask')}
                        onReviewAppeals={() => setModal('appealReview')}
                        onTopUp={() => setModal('topUp')}
                        isRoot={role === 'root'}
                        onBalanceChanged={() => { api.getMe().then((me) => setBalance(me.balance)).catch(() => {}); }}
                        canCreateTask={true}
                        tasksVersion={tasksVersion}
                        referralCode={refCode}
                        referralCount={refCount}
                        botUsername={botUsername}
                    />
                )}
                {view === 'bigGiveaway' && (
                    <BigGiveawayView
                        onNavigate={goView}
                        onShowRules={() => setModal('giveawayRules')}
                        target={giveawayTarget}
                        bank={bigBank}
                        autoShowRules={bigAutoRules}
                    />
                )}
                {view === 'admin' && isAdmin && (
                    <AdminPanelView onNavigate={goView} role={role} />
                )}
            </div>

            {/* ----- Modals ------------------------------------------------------ */}
            {modal === 'notifications' && (
                <NotificationsModal
                    items={notifs}
                    onClose={async () => {
                        // Mark everything as read on close so the bell count clears.
                        try { await api.markNotificationsRead({ all: true }); } catch { /* offline */ }
                        setUnreadCount(0);
                        closeModal();
                    }}
                    onDelete={(id) => {
                        // Server delete first; otherwise the next 10s poll
                        // would re-fetch the same row and the notif would
                        // "пропасть и снова появиться".
                        setNotifs((n) => n.filter((x) => x.id !== id));
                        api.deleteNotifications({ ids: [id] }).catch(() => {});
                    }}
                    onAction={() => closeModal()}
                />
            )}
            {modal === 'withdraw' && (
                <WithdrawModal
                    balance={balance}
                    minAmount={settings?.withdraw_min}
                    onClose={closeModal}
                    onSubmitted={handleWithdrawSubmitted}
                />
            )}
            {modal === 'createBattle' && (
                <CreateBattleModal
                    onClose={closeModal}
                    onCreate={async ({ bet, opponents, highlight }) => {
                        try {
                            const r = await api.createBattle(newIdempotencyKey(), {
                                stake: Math.round(bet),
                                slots: Math.max(2, opponents + 1),
                                vip: highlight,
                            });
                            const me = await api.getMe();
                            setBalance(me.balance);
                            // Drop the creator straight into the sit-in lobby
                            // so they can wait for opponents (ТЗ: "после
                            // создания попасть и сидеть в этой комнате").
                            if (r?.id) {
                                setActiveBattleId(r.id);
                                setView('battleLobby');
                            }
                        } catch {
                            setBalance((b) => b - bet - (highlight ? 5 : 0));
                        }
                        closeModal();
                    }}
                    minBet={5}
                    highlightFee={5}
                    balance={balance}
                />
            )}
            {modal === 'vipWarning' && (
                <VipWarningModal
                    onClose={closeModal}
                    onContinue={() => setModal('createTask')}
                />
            )}
            {modal === 'createTask' && (
                <CreateTaskModal
                    onClose={closeModal}
                    onPublish={async (r) => {
                        closeModal();
                        setTasksVersion((v) => v + 1);
                        try { const me = await api.getMe(); setBalance(me.balance); } catch { /* offline */ }
                        setResultPayload({ kind: 'taskCreated', amount: r.totalCost, detail: r.status === 'pending' ? 1 : 0 });
                    }}
                    role={role}
                    commissionPct={settings?.task_commission_pct ?? 30}
                    holdPricePerHour={settings?.task_hold_price_per_hour ?? 0.1}
                />
            )}
            {modal === 'taskExec' && activeTask && (
                <TaskExecModal
                    task={activeTask}
                    onClose={() => {
                        setActiveTask(null);
                        closeModal();
                    }}
                    onComplete={handleTaskComplete}
                />
            )}
            {modal === 'appealReview' && (
                <AppealReviewModal
                    onClose={closeModal}
                    onChanged={() => setTasksVersion((v) => v + 1)}
                />
            )}
            {modal === 'deleteTask' && (
                <ConfirmDeleteModal onClose={closeModal} onConfirm={closeModal} />
            )}
            {modal === 'giveawayRules' && (
                <GiveawayRulesModal
                    onClose={() => { closeModal(); setBigAutoRules(false); }}
                    freshRefs={freshRefs}
                    onGoToReferrals={() => {
                        closeModal();
                        setBigAutoRules(false);
                        setView('bonuses');
                    }}
                />
            )}
            {modal === 'profileEmoji' && (
                <EmojiPickerModal current={avatar} onClose={closeModal} onPick={setAvatar} />
            )}
            {modal === 'topUp' && (
                <TopUpModal
                    onClose={closeModal}
                    onResult={(info) => {
                        // The webhook is the source of truth for crediting.
                        // The openInvoice callback can fire `paid` BEFORE the
                        // backend processes the webhook (race), and CryptoBot
                        // *always* reports `pending` since we just opened an
                        // external link. So a single getMe() right here would
                        // routinely catch the old balance and leave the pill
                        // stale until the next 10s notification poll.
                        // Instead: kick off a short retry-poll that pings
                        // /api/me until the balance increases or we time out.
                        if (info.status !== 'cancelled' && info.status !== 'failed') {
                            void pollBalanceForCredit(info.amount);
                        }
                        closeModal();
                    }}
                />
            )}

            {resultPayload?.kind === 'boxWin' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Победа"
                    icon="🎉"
                    headline="Поздравляем!"
                    desc={`Вы выиграли ${resultPayload.amount} BSP.`}
                    accent="gold"
                    actionLabel="Забрать"
                />
            )}
            {resultPayload?.kind === 'fastLose' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Результат"
                    icon="😢"
                    headline="Не повезло"
                    desc="Билет пуст. Ставка ушла в общий банк."
                    accent="red"
                    actionLabel="Закрыть"
                />
            )}
            {resultPayload?.kind === 'fastNoFunds' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Недостаточно BSP"
                    icon="⭐"
                    headline="Нужен баланс на билет"
                    desc="Пополни счёт и возвращайся за билетом."
                    accent="gold"
                    actionLabel="Пополнить"
                    onAction={() => { setResultPayload(null); setModal('topUp'); }}
                />
            )}
            {resultPayload?.kind === 'withdrawQueued' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Заявка принята"
                    icon="📦"
                    headline="Ордер на продажу создан"
                    desc={`Сумма ${resultPayload.amount} BSP. Следите за статусом в профиле.`}
                    accent="green"
                    actionLabel="ОК"
                />
            )}
            {resultPayload?.kind === 'taskDone' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Задание принято"
                    icon="✅"
                    headline="Награда зачислена"
                    desc={`+${resultPayload.amount} BSP уже на балансе.`}
                    accent="green"
                />
            )}
            {resultPayload?.kind === 'taskHolding' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Подписка подтверждена"
                    icon="⏳"
                    headline="Идёт холд"
                    desc={`+${resultPayload.amount} BSP придут после холда${resultPayload.holdUntil
                        ? ` (${new Date(resultPayload.holdUntil).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})`
                        : ''}. Не отписывайтесь до конца холда.`}
                    accent="gold"
                />
            )}
            {resultPayload?.kind === 'taskAppeal' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Запрос отправлен"
                    icon="⚖️"
                    headline={resultPayload.detail === 2 ? 'Пересмотр у администрации' : 'Пересмотр у заказчика'}
                    desc="Вы получите уведомление, когда по запросу примут решение."
                    accent="gold"
                />
            )}
            {resultPayload?.kind === 'taskCreated' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title={resultPayload.detail === 1 ? 'Задание на модерации' : 'Задание опубликовано'}
                    icon={resultPayload.detail === 1 ? '🕵️' : '🚀'}
                    headline={resultPayload.detail === 1 ? 'Отправлено на проверку' : 'Уже в списке заданий'}
                    desc={resultPayload.detail === 1
                        ? `Списано ${resultPayload.amount ?? 0} BSP. После проверки задание появится в списке — или вернётся к вам с причиной отказа.`
                        : 'Исполнители уже видят его в разделе «Задания».'}
                    accent={resultPayload.detail === 1 ? 'gold' : 'green'}
                />
            )}
            {resultPayload?.kind === 'taskPending' && (
                <ResultPopup
                    onClose={() => setResultPayload(null)}
                    title="Заявка отправлена"
                    icon="📋"
                    headline="На проверке у модератора"
                    desc="Вы получите уведомление в боте после рассмотрения заявки."
                    accent="gold"
                />
            )}
        </>
    );
}

/**
 * True when the current page is running locally (Vite dev, Playwright, the
 * staging preview) and we shouldn't gate the UI behind the Telegram bridge.
 * Detection is permissive: a `?dev=1` query, localhost hostnames, or the
 * VITE_DEV_BYPASS env flag all turn the stub off.
 */
function isDevHost(): boolean {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.has('dev') || params.has('startapp') || params.has('ref')) return true;
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return true;
    } catch { /* SSR-safe no-op */ }
    return Boolean((import.meta as { env?: { VITE_DEV_BYPASS?: string } }).env?.VITE_DEV_BYPASS);
}

/** Shape conversion: backend → existing local WithdrawRecord type. */
function toLocalWithdrawal(w: WithdrawalDto): WithdrawRecord {
    return {
        id: w.id,
        amount: w.amount,
        method: w.method,
        target: w.target,
        status:
            w.status === 'paid' ? 'sent' :
            w.status === 'rejected' || w.status === 'failed' ? 'failed' :
            'pending',
        createdAt: w.created_at,
    };
}
